import type {
  PlantingPlan,
  PlantingPlanCultivar,
  PlantingPlanSaveData,
  Pollinator,
  RowGroup,
} from '../../types/orchard';

/** The editable half of a planting plan, held while nobody has saved it. */
export interface PlanDraft {
  first_row_start_point: [number, number] | null;
  first_row_end_point: [number, number] | null;
  /** 0 = not entered: the server fills the boundary with as many rows as fit. */
  row_count: number;
  row_spacing_meters: number;
  tree_spacing_meters: number;
  planting_pattern: RowGroup[];
  default_pollinators: Pollinator[];
}

export const emptyPlanDraft = (): PlanDraft => ({
  first_row_start_point: null,
  first_row_end_point: null,
  row_count: 0,
  row_spacing_meters: 0,
  tree_spacing_meters: 0,
  planting_pattern: [],
  default_pollinators: [],
});

export const draftFromPlan = (p: PlantingPlan): PlanDraft => ({
  first_row_start_point: p.plan.first_row_start_point,
  first_row_end_point: p.plan.first_row_end_point,
  row_count: p.plan.row_count ?? 0,
  row_spacing_meters: p.plan.row_spacing_meters ?? 0,
  tree_spacing_meters: p.plan.tree_spacing_meters ?? 0,
  planting_pattern: p.plan.planting_pattern ?? [],
  default_pollinators: p.plan.default_pollinators ?? [],
});

/** Groups and pollinators with no cultivar picked yet are dropped: the server
 *  refuses a pollinator without one, and a half-filled row is still mid-edit. */
export const draftToSave = (d: PlanDraft): PlantingPlanSaveData => {
  const groups = d.planting_pattern.filter((g) => g.row_count > 0);
  const pollinators = d.default_pollinators.filter((p) => p.cultivar_id > 0);
  return {
    first_row_start_point: d.first_row_start_point,
    first_row_end_point: d.first_row_end_point,
    row_count: d.row_count || null,
    row_spacing_meters: d.row_spacing_meters || null,
    tree_spacing_meters: d.tree_spacing_meters || null,
    planting_pattern: groups.length ? groups.map(({ cultivar_id, row_count }) => ({ cultivar_id, row_count })) : null,
    default_pollinators: pollinators.length ? pollinators : null,
  };
};

/** Enough is known to lay out rows: a row 1 line and a spacing between rows. */
export const draftCanLayOut = (d: PlanDraft): boolean =>
  Boolean(d.first_row_start_point && d.first_row_end_point && d.row_spacing_meters > 0);

export const cultivarDisplayName = (c: PlantingPlanCultivar): string =>
  c.trade_name ? `${c.name} (${c.trade_name})` : c.name;
